import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';

/**
 * One row of the autocomplete dropdown. `html` is safe to bind with [innerHTML]:
 * it is either escaped text or the search API's highlighted title (only <em>).
 */
export interface SuggestItem {
  kind: 'recent' | 'video' | 'category' | 'popular';
  label: string;
  html: string;
  meta?: string;
  video?: any;
}

/**
 * Talks to the search API's suggest/recent endpoints (see
 * vplayed-jkp-search-api/SEARCH-API.md) for the header box and the /search page.
 */
@Injectable({
  providedIn: 'root'
})
export class SearchSuggestService {

  constructor(private apiService: ApiService) { }

  /** Flat list in display order: recent, videos, categories, popular. Empty q -> recent + popular only. */
  fetch(value: any, limit = 6): Observable<SuggestItem[]> {
    const q = (value === undefined || value === null ? '' : String(value)).trim();
    return this.apiService.callGetAPI('search/suggest?q=' + encodeURIComponent(q) + '&limit=' + limit, environment.searchApiUrl)
      .pipe(map(data => (data && data['error'] === false && data['response']) ? this.flatten(data['response']) : []));
  }

  /**
   * Inline ("ghost") completion: the rest of the best suggestion that starts
   * with what was typed, or '' when nothing does. Own history first, then what
   * others search, then titles/categories. Case-insensitive, keeps the typed text as is.
   */
  inlineCompletion(items: SuggestItem[], typed: any): string {
    const t = (typed === undefined || typed === null ? '' : String(typed)).replace(/^\s+/, '');
    if (t.length < 2 || !items || !items.length) {
      return '';
    }
    const lower = t.toLowerCase();
    const order = ['recent', 'popular', 'video', 'category'];
    for (let k = 0; k < order.length; k++) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind !== order[k] || !item.label) {
          continue;
        }
        const label = String(item.label).replace(/\s+/g, ' ');
        if (label.length > t.length && label.toLowerCase().indexOf(lower) === 0) {
          return label.slice(t.length);
        }
      }
    }
    return '';
  }

  removeRecent(query: string): Observable<any> {
    return this.apiService.callDeleteAPI('search/recent?q=' + encodeURIComponent(query), environment.searchApiUrl);
  }

  clearRecent(): Observable<any> {
    return this.apiService.callDeleteAPI('search/recent', environment.searchApiUrl);
  }

  private flatten(r: any): SuggestItem[] {
    const list: SuggestItem[] = [];
    (r.recent || []).forEach(x => list.push({ kind: 'recent', label: x.query, html: this.escape(x.query) }));
    (r.videos || []).forEach(v => list.push({
      kind: 'video', label: v.title, video: v,
      html: v.title_highlighted || this.escape(v.title),
      meta: [v.category, v.video_duration].filter(Boolean).join(' · ')
    }));
    (r.categories || []).forEach(c => list.push({ kind: 'category', label: c.name, html: this.escape(c.name), meta: String(c.count) }));
    (r.popular || []).forEach(p => list.push({ kind: 'popular', label: p.query, html: this.escape(p.query) }));
    return list;
  }

  private escape(s: any): string {
    return (s === undefined || s === null ? '' : String(s))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
